import { useEffect, useState } from "react";
import api, { API, formatErr } from "@/lib/api";
import { PageHeader, Section } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminReports() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [userId, setUserId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/users/employees"), api.get("/projects")])
      .then(([u, p]) => { setUsers(u.data); setProjects(p.data); });
  }, []);

  const run = async () => {
    try {
      const q = new URLSearchParams({ from_date: from, to_date: to });
      if (userId) q.set("user_id", userId); if (projectId) q.set("project_id", projectId);
      const r = await api.get(`/reports/timesheet?${q}`); setRows(r.data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const download = async (fmt) => {
    try {
      const q = new URLSearchParams({ from_date: from, to_date: to, format: fmt });
      if (userId) q.set("user_id", userId); if (projectId) q.set("project_id", projectId);
      const r = await fetch(`${API}/reports/timesheet?${q}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error("Failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `timesheet_${from}_${to}.${fmt === "xlsx" ? "xlsx" : fmt}`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const totalH = rows.reduce((s, r) => s + (r.Hours || 0), 0);
  const billH = rows.reduce((s, r) => s + (r.Billable === "Yes" ? r.Hours || 0 : 0), 0);

  return (
    <div data-testid="admin-reports">
      <PageHeader overline="Analytics" title="Reports" subtitle="Timesheet reports with export to CSV, Excel, PDF." />
      <Section title="Filters"
        right={<div className="flex gap-2">
          <Button onClick={() => download("csv")} variant="outline" className="rounded-none" data-testid="export-csv">CSV</Button>
          <Button onClick={() => download("xlsx")} variant="outline" className="rounded-none" data-testid="export-xlsx">Excel</Button>
          <Button onClick={() => download("pdf")} variant="outline" className="rounded-none" data-testid="export-pdf">PDF</Button>
        </div>}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div><div className="eyebrow mb-1">From</div><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none" data-testid="rep-from" /></div>
          <div><div className="eyebrow mb-1">To</div><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none" data-testid="rep-to" /></div>
          <div><div className="eyebrow mb-1">Employee</div>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="rep-user">
              <option value="">All</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div><div className="eyebrow mb-1">Project</div>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="w-full h-10 border border-[#E5E5E5] px-2 bg-white" data-testid="rep-project">
              <option value="">All</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="flex items-end"><Button onClick={run} className="w-full rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white" data-testid="run-report">Generate</Button></div>
        </div>
      </Section>

      <div className="grid grid-cols-3 gap-3 my-4">
        <div className="bg-white border border-[#E5E5E5] p-4"><div className="eyebrow">Rows</div><div className="font-head text-3xl font-semibold">{rows.length}</div></div>
        <div className="bg-white border border-[#E5E5E5] p-4"><div className="eyebrow">Total Hours</div><div className="font-head text-3xl font-semibold">{totalH.toFixed(1)}</div></div>
        <div className="bg-white border border-[#E5E5E5] p-4"><div className="eyebrow">Billable</div><div className="font-head text-3xl font-semibold text-[#FF5500]">{billH.toFixed(1)}</div></div>
      </div>

      <Section title="Results">
        <div className="overflow-x-auto">
          <table className="w-full table-dense">
            <thead>
              <tr className="border-b-2 border-[#1F4E8C] text-left">
                {["Date","Employee","Department","Project","Task","Hours","Billable","Description"].map((h) => <th key={h} className="eyebrow">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[#F0F0EE]">
                  <td>{r.Date}</td><td>{r.Employee}</td><td>{r.Department}</td>
                  <td>{r.Project}</td><td>{r.Task}</td><td className="font-semibold">{r.Hours}</td>
                  <td>{r.Billable}</td><td className="max-w-[260px] truncate">{r.Description}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-neutral-500">Click Generate to run report</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
